// src/components/common/StickySearchPanel.jsx
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { UpOutlined } from '@ant-design/icons';
import './StickySearchPanel.css';

/**
 * Sticky-панель для формы поиска с красивым переходом при прокрутке.
 * 
 * @param {React.ReactNode} children - Развёрнутая форма поиска
 * @param {React.ReactNode} compactContent - Содержимое компактной панели (миниатюра, текст запроса и т.д.)
 * @param {number} threshold - Порог прокрутки для сворачивания (по умолчанию 80px)
 * @param {number} offsetTop - Отступ сверху для fixed панели
 * @param {boolean} forceExpanded - Принудительно держать развёрнутым (для режима с развёрнутой формой)
 * @param {boolean} overlayOnly - Режим компактной панели поверх старой формы (expanded не рендерится)
 */
const StickySearchPanel = ({
  children,
  compactContent,
  threshold = 80,
  offsetTop = 12,
  forceExpanded = false,
  overlayOnly = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [manualExpanded, setManualExpanded] = useState(false);
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    setIsVisible(currentScrollY > threshold);
    
    // Если скролл вверх и пользователь близко к верху - разворачиваем
    if (currentScrollY <= threshold / 2) {
      setIsCollapsed(false);
      setManualExpanded(false);
    } else if (currentScrollY > threshold && !manualExpanded) {
      setIsCollapsed(true);
    }

    // Добавляем тень когда прилип
    setIsSticky(currentScrollY > 0);
  }, [threshold, manualExpanded]);

  useEffect(() => {
    // Проверяем начальную позицию скролла
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Если forceExpanded - всегда развёрнут
  useEffect(() => {
    if (forceExpanded) {
      setIsCollapsed(false);
      setManualExpanded(false);
    }
  }, [forceExpanded]);

  // Измеряем высоту панели, чтобы добавить компенсирующий отступ под ней
  useLayoutEffect(() => {
    if (!panelRef.current || overlayOnly) return;
    const measure = () => {
      const rect = panelRef.current.getBoundingClientRect();
      setPanelHeight(rect.height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(panelRef.current);
    return () => ro.disconnect();
  }, [overlayOnly]);

  const handleCompactClick = () => {
    setIsCollapsed(false);
    setManualExpanded(true);
    // Плавно проскроллить наверх
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const classNames = [
    'sticky-search-panel',
    isCollapsed ? 'is-collapsed' : '',
    isSticky ? 'is-sticky' : '',
  ].filter(Boolean).join(' ');

  // Режим компактной панели поверх старого макета: только compactContent и только после скролла
  if (overlayOnly) {
    if (!isVisible) return null;
    return (
      <div
        ref={panelRef}
        className={`${classNames} overlay-only`}
        style={{ '--sticky-top': `${offsetTop}px` }}
      >
        <div className="sticky-search-panel__compact">
          {compactContent}
          <button
            type="button"
            className="sticky-search-panel__expand-btn"
            onClick={handleCompactClick}
            title="Развернуть форму"
            aria-label="Развернуть форму поиска"
          >
            <UpOutlined />
            <span className="sticky-search-panel__expand-btn-label">К форме</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={panelRef}
        className={classNames}
        style={{ '--sticky-top': `${offsetTop}px` }}
      >
        {/* Развёрнутая форма */}
        <div className="sticky-search-panel__expanded">
          {children}
        </div>

        {/* Компактная панель */}
        <div className="sticky-search-panel__compact">
          {compactContent}
          
          <button
            type="button"
            className="sticky-search-panel__expand-btn"
            onClick={handleCompactClick}
            title={isCollapsed ? 'Развернуть форму' : 'Свернуть форму'}
            aria-label={isCollapsed ? 'Развернуть форму поиска' : 'Свернуть компактную панель'}
          >
            <UpOutlined />
            <span className="sticky-search-panel__expand-btn-label">Развернуть</span>
          </button>
        </div>
      </div>
      {/* Компенсирующий отступ, чтобы результаты не заходили под fixed панель */}
      <div style={{ height: panelHeight }} aria-hidden="true" />
    </>
  );
};

export default StickySearchPanel;
